/**
 * Autobuild session detail — one run's BigQuery flight-recorder trail,
 * chronological (ascending), with the session's overall facts on top.
 */
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink } from 'lucide-react';
import type { AutobuildKind, AutobuildLogEvent, AutobuildSessionTicket } from '@a7y/types';
import { CDID_TERMINAL_LIFECYCLE_STATES } from '@a7y/types';
import { api } from '@/lib/api';
import { PageHeader } from '@/components/PageHeader';
import {
  ACTIVITY_COMPONENT_LABELS,
  fmtDuration,
  fmtLocal,
  fmtUtc,
  localZoneAbbrev,
  KIND_LABELS,
  LIFECYCLE_STATE_HINTS,
  LIFECYCLE_STATE_LABELS,
  LIFECYCLE_STATE_STYLES,
  sessionDurationSeconds,
  sortByIdDesc,
  useNow,
  PRIORITY_STYLES,
} from './shared';
import { OutcomeBadge } from './OutcomeBadge';
import { RunnerBadge } from './RunnerBadge';
import { SessionQuotaUsage } from './SessionQuotaUsage';
import { SessionThroughputSection } from './SessionThroughputSection';
import { StageChips } from './StageChips';
import { TriggerBadge } from './TriggerBadge';
import { isCrossCutting, normalizePhase, phaseInfo, type PhaseInfo } from './phases';

const SEVERITY_ROW: Record<AutobuildLogEvent['severity'], string> = {
  info: 'border-l-zinc-300 dark:border-l-zinc-700',
  warning: 'border-l-amber-400 text-amber-800 dark:text-amber-300',
  error: 'border-l-red-500 text-red-700 dark:text-red-300',
};

const OUTCOME_COLOR = {
  done: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  failed: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  inflight: 'bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300',
  attention: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  carried: 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400',
} as const;

/** Solid dot for the summary row. Declared rather than derived from the chip
 *  classes above — picking a colour out of a class string by position breaks
 *  silently the first time someone reorders it. */
const OUTCOME_DOT: Record<keyof typeof OUTCOME_COLOR, string> = {
  done: 'bg-emerald-500',
  failed: 'bg-red-500',
  inflight: 'bg-blue-500',
  attention: 'bg-amber-500',
  carried: 'bg-zinc-400',
};

/**
 * Every ticket outcome the sessions actually emit, mapped to a meaning.
 *
 * Sourced by measuring `phase='ticket'` rows over 30 days rather than guessing:
 * ten outcomes had no entry here and fell through to the neutral default, so a
 * ticket that FAILED TO SHIP rendered identically to one whose outcome the
 * console simply did not recognise. `not-shipped` is the case that matters —
 * on APD 2026-08-03-00-39 three tickets went out with the deploy and did not
 * make it, and nothing on the page said so.
 *
 * `tone` drives colour; `hint` becomes the chip's hover title, because several
 * of these ("carried", "monitor", "evaluated") are not self-explanatory.
 * `label` replaces machine-oriented lifecycle names where the operator-facing
 * distinction matters (queued vs actively implementing vs review).
 */
const TICKET_OUTCOMES: Record<
  string,
  { tone: keyof typeof OUTCOME_COLOR; hint: string; label?: string }
> = {
  // Resolved — counted in the list page's ticket column.
  shipped: { tone: 'done', hint: 'Deployed to production by this session' },
  landed: { tone: 'done', hint: 'Merged to the release branch by this session' },
  adopted: { tone: 'done', hint: "Picked up from an earlier session's orphaned branch and landed" },
  superseded: { tone: 'done', hint: 'Closed as superseded — resolved without shipping code' },
  succeeded: {
    tone: 'done',
    label: 'Succeeded',
    hint: 'Implementation completed and the ticket reached Done in Dev',
  },
  // Failed — went out with the deploy and did not make it.
  'not-shipped': { tone: 'failed', hint: 'Was in the deploy and did NOT reach production' },
  // In flight when the row was written; replaced by the real outcome at the end.
  batched: {
    tone: 'inflight',
    label: 'Queued',
    hint: 'Claimed by this session and waiting for an implementation slot',
  },
  deploying: { tone: 'inflight', hint: 'Queued in the deploy before dispatch (APD Phase 4)' },
  // A7Y-3962 — the two outcomes attribution recovers rather than reads. Neither
  // is written as a ticket row by anyone: `admitted` comes from the ingress
  // scheduler's `lane-admission` payload, and `implementing` from the branch a
  // worker names in its own log. They exist because the sessions that do the
  // admitting and the implementing emit no ticket rows at all — measured over
  // the 7 days to 2026-09-13, 0 of 1,429 ingress-scheduler sessions and 1 of
  // 254 workers did.
  admitted: {
    tone: 'inflight',
    label: 'Admitted',
    hint: 'Admitted to a lane by this cycle and dispatched to a worker',
  },
  dispatched: {
    tone: 'inflight',
    label: 'Dispatched',
    hint: 'Handed to a CDID run by the hot lane; the lane does not implement it',
  },
  implementing: {
    tone: 'inflight',
    label: 'Implementing',
    hint: 'This worker built the branch carrying this ticket',
  },
  'in-progress': {
    tone: 'inflight',
    label: 'Implementing',
    hint: 'A CDID implementation group is actively working this ticket',
  },
  'in-review': {
    tone: 'inflight',
    label: 'In review',
    hint: 'Implementation finished; Integration/Karen review is active',
  },
  'fix-in-flight': {
    tone: 'inflight',
    label: 'Fix in progress',
    hint: 'A review fix is actively being applied for this issue',
  },
  // Needs a human.
  parked: { tone: 'attention', hint: 'Stopped and handed to the human queue' },
  blocked: { tone: 'attention', hint: 'Blocked — could not proceed' },
  // Touched, but no code outcome. Deliberately quiet.
  deferred: {
    tone: 'carried',
    hint: 'Eligible but not taken this run — carried to a later session',
  },
  // Stage-maps to `deferred` alongside the literal `deferred` outcome
  // (`cycle_stages.ts`'s `OUTCOME_TO_STAGE`) — a distinct producer outcome,
  // not a synonym, so it keeps its own label and hint here.
  requeued: { tone: 'carried', hint: 'Returned to the queue for another attempt' },
  evaluated: { tone: 'carried', hint: 'Assessed during selection; not batched' },
  monitor: { tone: 'carried', hint: 'Being watched rather than worked' },
  filed: { tone: 'carried', hint: 'Created by this session — not deployed by it' },
  'follow-up': { tone: 'carried', hint: 'Raised as a follow-up' },
  'follow-up-filed': { tone: 'carried', hint: 'Follow-up ticket created' },
  mentioned: { tone: 'carried', hint: 'Referenced in the log; no explicit outcome recorded' },
};

const TICKET_OUTCOME_STYLES: Record<string, string> = Object.fromEntries(
  Object.entries(TICKET_OUTCOMES).map(([k, v]) => [k, OUTCOME_COLOR[v.tone]])
);

/** Counts by outcome, most numerous first — the one-line reconciliation with
 *  the run-history list, which shows only the RESOLVED subset. */
function outcomeBreakdown(tickets: { outcome: string }[]): { outcome: string; n: number }[] {
  const counts = new Map<string, number>();
  for (const t of tickets) counts.set(t.outcome, (counts.get(t.outcome) ?? 0) + 1);
  return [...counts.entries()]
    .map(([outcome, n]) => ({ outcome, n }))
    .sort((a, b) => b.n - a.n || a.outcome.localeCompare(b.outcome));
}
const TICKET_OUTCOME_DEFAULT = 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400';

function ticketOutcomeLabel(outcome: string): string {
  return TICKET_OUTCOMES[outcome]?.label ?? outcome;
}

/**
 * A ticket's own stage chip (A7Y-3962 D4/D5) — distinct from `StageChips`,
 * which summarises a CYCLE's tickets by count; a single ticket has no count
 * to show, so this renders when it entered instead: a terminal ticket shows
 * WHEN it landed (there is nothing left to time), a non-terminal one shows
 * HOW LONG it has been there, computed client-side from `stageEnteredAt`.
 * Null when the ticket carries no attributed stage (exclusion 6, or an
 * outcome — `dispatched`, a legacy "mentioned" row — that maps to none).
 */
function ticketStageChip(t: AutobuildSessionTicket, now: number) {
  if (!t.stage || !t.stageEnteredAt) return null;
  const terminal = CDID_TERMINAL_LIFECYCLE_STATES.has(t.stage);
  const label = LIFECYCLE_STATE_LABELS[t.stage];
  const enteredLocal = fmtLocal(t.stageEnteredAt);
  const title = `${LIFECYCLE_STATE_HINTS[t.stage]} — entered ${enteredLocal}`;
  const text = terminal
    ? `${label} · entered ${enteredLocal}`
    : `${label} · ${fmtDuration(Math.max(0, (now - Date.parse(t.stageEnteredAt)) / 1000))}`;
  return (
    <span
      className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 whitespace-nowrap ${LIFECYCLE_STATE_STYLES[t.stage]}`}
      title={title}
    >
      {text}
    </span>
  );
}

/**
 * "bundle-id · attempt N · account" — the D1 drill-in this ticket carries
 * when its cycle admitted it in a bundle. Renders only the parts a fact
 * actually named (`AutobuildSessionTicket`'s own null-means-never-recorded
 * contract), and nothing at all when the ticket carries no bundle id.
 */
function ticketBundleLine(t: AutobuildSessionTicket): string | null {
  if (t.bundleId == null) return null;
  return [t.bundleId, t.attempt != null ? `attempt ${t.attempt}` : null, t.implementationAccount]
    .filter((part): part is string => Boolean(part))
    .join(' · ');
}

/**
 * When this cycle admitted the ticket — the opening of the story its stage chip
 * ends. A7Y-3962's drill-in question is "which tickets did this cycle process",
 * and for a settled ticket the honest answer is a span, not an instant: cycle
 * `ingress-34658548021-1` admitted A7Y-3924 at 23:35 and its lander wave merged
 * it at 09:47 the next morning, 10 hours and three sessions later.
 *
 * Rendered from `admittedAt` rather than from the stage chip because the chip
 * shows only the CURRENT stage: once a ticket lands, the admission time is
 * gone from the row, which is exactly when the lag is worth seeing.
 */
function ticketAdmittedLine(t: AutobuildSessionTicket): string | null {
  return t.admittedAt ? `admitted ${fmtLocal(t.admittedAt)}` : null;
}

// `implementing` is attributed from a worker's own branch (A7Y-3962) and means
// exactly what `in-progress` means one layer down, so it shares the section.
const ACTIVE_TICKET_OUTCOMES = new Set([
  'in-progress',
  'in-review',
  'fix-in-flight',
  'implementing',
]);
// `admitted` and `dispatched` are claims on a ticket that has not reached an
// implementation slot yet — the same state `batched` describes, recorded by the
// ingress scheduler and the hot lane respectively (A7Y-3962).
const QUEUED_TICKET_OUTCOMES = new Set(['batched', 'admitted', 'dispatched']);
const SUCCEEDED_TICKET_OUTCOMES = new Set([
  'succeeded',
  'shipped',
  'landed',
  'adopted',
  'superseded',
]);

interface TicketLifecycleSection {
  key: 'succeeded' | 'active' | 'queued' | 'attention' | 'other';
  label: string;
  description: string;
  tickets: AutobuildSessionTicket[];
}

const TICKET_SECTION_HEADER_STYLES: Record<TicketLifecycleSection['key'], string> = {
  succeeded: 'border-l-4 border-l-emerald-500 bg-emerald-50/80 dark:bg-emerald-950/25',
  active: 'border-l-4 border-l-cyan-500 bg-cyan-50/80 dark:bg-cyan-950/25',
  queued: 'border-l-4 border-l-blue-500 bg-blue-50/70 dark:bg-blue-950/20',
  attention: 'border-l-4 border-l-amber-500 bg-amber-50/80 dark:bg-amber-950/25',
  other: 'border-l-4 border-l-zinc-400 bg-zinc-100/80 dark:bg-zinc-800/45',
};

const TICKET_SECTION_LABEL_STYLES: Record<TicketLifecycleSection['key'], string> = {
  succeeded: 'text-emerald-800 dark:text-emerald-300',
  active: 'text-cyan-800 dark:text-cyan-300',
  queued: 'text-blue-800 dark:text-blue-300',
  attention: 'text-amber-800 dark:text-amber-300',
  other: 'text-zinc-700 dark:text-zinc-300',
};

/**
 * Keep active work visually separate from tickets merely claimed by the run.
 * Linear's `In Dev` state cannot make this distinction: CDID advances a ticket
 * before its implementation slot starts, and a stranded ticket may stay there.
 * The flight recorder's latest structured lifecycle row is the source of truth.
 */
function ticketLifecycleSections(tickets: AutobuildSessionTicket[]): TicketLifecycleSection[] {
  const succeeded: AutobuildSessionTicket[] = [];
  const active: AutobuildSessionTicket[] = [];
  const queued: AutobuildSessionTicket[] = [];
  const attention: AutobuildSessionTicket[] = [];
  const other: AutobuildSessionTicket[] = [];

  for (const ticket of tickets) {
    const tone = TICKET_OUTCOMES[ticket.outcome]?.tone;
    if (SUCCEEDED_TICKET_OUTCOMES.has(ticket.outcome)) succeeded.push(ticket);
    else if (ACTIVE_TICKET_OUTCOMES.has(ticket.outcome)) active.push(ticket);
    else if (QUEUED_TICKET_OUTCOMES.has(ticket.outcome)) queued.push(ticket);
    else if (tone === 'attention' || tone === 'failed') attention.push(ticket);
    else other.push(ticket);
  }

  const sections: TicketLifecycleSection[] = [
    {
      key: 'succeeded',
      label: 'Succeeded',
      description: 'Resolved successfully by this session.',
      tickets: succeeded,
    },
    {
      key: 'attention',
      label: 'Attention needed',
      description: 'Blocked, failed, or waiting on human action.',
      tickets: attention,
    },
    {
      key: 'other',
      label: 'Deferred / other',
      description: 'Removed from this run’s active queue without a successful resolution.',
      tickets: other,
    },
    {
      key: 'active',
      label: 'In development',
      description: 'An implementation or review slot is actively working these tickets.',
      tickets: active,
    },
    {
      key: 'queued',
      label: 'Queued',
      description:
        'Claimed or admitted by this CDID run, waiting for an implementation slot.',
      tickets: queued,
    },
  ];
  return sections.filter((section) => section.tickets.length > 0);
}

function fmtLogTime(iso: string): string {
  return fmtLocalClock(Date.parse(iso));
}

/**
 * HH:MM:SS in the viewer's timezone (TPF, 2026-08-02). These rows rendered
 * UTC until now — the sessions' native clock, which lines up with Slack and
 * BigQuery without arithmetic. But reading a run is far more common than
 * cross-referencing one, and doing the offset in your head on every row is a
 * tax on the common case. The local clock takes the display; the UTC instant
 * moves to each row's hover title, so correlation costs one hover instead of
 * one subtraction per row.
 */
function fmtLocalClock(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

/** The same instant in UTC — the hover title behind every log timestamp. */
function utcTooltip(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, '0');
  const clock = `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}`;
  return `${clock} UTC — matches Slack and the BigQuery flight recorder`;
}

interface LogGroup {
  phase: string;
  info: PhaseInfo | null;
  events: AutobuildLogEvent[];
  startMs: number;
  /** When the phase handed off. Null while it is still the open phase of a running session. */
  endMs: number | null;
}

/**
 * Slice the chronological trail into contiguous phase sections.
 *
 * Only a structural phase (`part-c`, `phase-3`) opens a section. Cross-cutting
 * rows — ticket outcomes, state changes, the runner's start/terminal — are
 * written *during* whatever phase is running, so they join the open section
 * rather than fragmenting it. Grouping by phase identity instead would scatter
 * a session across the page and destroy the chronology, and a phase that
 * genuinely runs twice deserves two sections.
 *
 * A section ends when the **next one opens**, not at its own last log row. A
 * phase routinely keeps working after its final row — Part B logs "npm install
 * starting" and then installs for three more minutes — so measuring row-span
 * undercounts badly, and reports 0s for any single-row section. Measured on
 * session 2026-07-29-12-44: Part B's rows span 0m53s but the phase occupied
 * 3m58s; Part A logged one row yet took 41s.
 */
function groupEvents(
  kind: AutobuildKind,
  events: AutobuildLogEvent[],
  sessionEnded: boolean
): LogGroup[] {
  const groups: LogGroup[] = [];
  for (const e of events) {
    const key = normalizePhase(e.phase);
    const open = groups[groups.length - 1];
    const opensSection = !isCrossCutting(e.phase) && (!open || open.phase !== key);
    if (opensSection || !open) {
      groups.push({
        phase: key,
        info: phaseInfo(kind, key),
        events: [e],
        startMs: Date.parse(e.ts),
        endMs: null,
      });
    } else {
      open.events.push(e);
    }
  }
  // Second pass: each section's boundary is the next section's first row. The
  // final section closes on its own last row — which for an ended session is
  // the runner's terminal row — and stays open while the session runs.
  groups.forEach((g, i) => {
    const next = groups[i + 1];
    if (next) g.endMs = next.startMs;
    else if (sessionEnded) g.endMs = Date.parse(g.events[g.events.length - 1].ts);
  });
  return groups;
}

function severityCounts(events: AutobuildLogEvent[]): { warnings: number; errors: number } {
  return {
    warnings: events.filter((e) => e.severity === 'warning').length,
    errors: events.filter((e) => e.severity === 'error').length,
  };
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-zinc-400">{label}</div>
      <div className="text-sm text-zinc-800 dark:text-zinc-200 mt-0.5">{value}</div>
    </div>
  );
}

export default function AutobuildSessionDetailPage() {
  const { kind = '', stamp = '' } = useParams();
  const now = useNow();
  const validKind = ['cdid', 'ahc', 'apd'].includes(kind);

  const detail = useQuery({
    queryKey: ['autobuild-session', kind, stamp],
    queryFn: () => api.getAutobuildSession(kind as AutobuildKind, stamp),
    enabled: validKind && stamp.length > 0,
    // Keep polling while the session is still running OR any stage is
    // non-terminal (A7Y-3962 D5) — a cycle settles hours after its session
    // ends (green→landed p90 5.9h, max 23h), written by other sessions, so
    // "the session finished" is not "the page is done changing".
    refetchInterval: (q) => {
      const s = q.state.data?.summary;
      if (!s) return false;
      if (s.outcome === 'running') return 30_000;
      const settling = s.stages.some((stage) => !CDID_TERMINAL_LIFECYCLE_STATES.has(stage.stage));
      return settling ? 30_000 : false;
    },
  });

  if (!validKind) {
    return <div className="p-6 text-sm text-red-600">Unknown routine "{kind}".</div>;
  }

  const summary = detail.data?.summary;
  const tickets = sortByIdDesc(detail.data?.tickets ?? []);
  const ticketSections = ticketLifecycleSections(tickets);
  const events = detail.data?.events ?? [];

  return (
    <div className="space-y-4">
      <div>
        <Link
          to={`/admin/automation/${kind}`}
          className="mb-4 inline-flex items-center text-sm text-zinc-500 hover:text-zinc-700 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="mr-1 h-4 w-4" /> All runs
        </Link>
        <PageHeader
          title={`${KIND_LABELS[kind]} session ${stamp}`}
          subtitle="BigQuery flight-recorder trail, chronological. All times in your timezone — hover any row for its UTC instant."
        />
      </div>

      {detail.isLoading && <div className="p-6 text-sm text-zinc-500">Loading session…</div>}

      {detail.isError && (
        <div className="p-4 rounded-lg border border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/40 text-sm text-red-700 dark:text-red-300">
          Session not found or failed to load — it may predate the flight recorder (2026-07-24) or
          be older than the 30-day window.
        </div>
      )}

      {summary && (
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-8 gap-4 grow">
              {/* The run-history table keys on the start time now — a stamp
                  like `ingress-34939121211-1` is a machine key, not something a
                  reader scans a list by. This is where it lives instead:
                  selectable, monospaced, next to the facts it identifies. */}
              <Fact
                label="Session id"
                value={<span className="font-mono text-xs break-all">{stamp}</span>}
              />
              <Fact label="Component" value={ACTIVITY_COMPONENT_LABELS[summary.component]} />
              <Fact
                label="Outcome"
                value={
                  <span className="flex items-center gap-1.5">
                    <OutcomeBadge outcome={summary.outcome} />
                    <TriggerBadge manuallyTriggered={summary.manuallyTriggered} />
                  </span>
                }
              />
              {/* Fact-derived, not session-derived (A7Y-3962 D5): a cycle's
                  session ends in seconds, its tickets settle hours later,
                  written by OTHER sessions. This never reads
                  `summary.outcome`. */}
              <Fact label="Stage" value={<StageChips stages={summary.stages} now={now} />} />
              <Fact
                label="Started"
                value={
                  <span title={`${fmtUtc(summary.startedAt)}`}>{fmtLocal(summary.startedAt)}</span>
                }
              />
              {summary.sessionLabel && <Fact label="Session label" value={summary.sessionLabel} />}
              <Fact
                label={summary.outcome === 'running' ? 'Running for' : 'Duration'}
                value={
                  <span className="tabular-nums">
                    {fmtDuration(sessionDurationSeconds(summary, now))}
                  </span>
                }
              />
              {/* The FULL runner id, not just the fleet badge. Tracing a hung
                  session needs the specific slot: run 33164417473 was pinned to
                  ukko-server-ghr-14-mtcqy0u1-2d2gx that way on 2026-08-28. The
                  list view carries only the badge; this is where the machine
                  belongs. Renders nothing at all for a session logged before the
                  marker existed rather than asserting a fleet. */}
              {summary.runnerHost != null && (
                <Fact
                  label="Runner"
                  value={
                    <span className="flex flex-col gap-1">
                      <RunnerBadge runnerHost={summary.runnerHost} />
                      {summary.runnerName && (
                        <span
                          className="font-mono text-[11px] break-all text-zinc-500 dark:text-zinc-400"
                          title={summary.runnerName}
                        >
                          {summary.runnerName}
                        </span>
                      )}
                    </span>
                  }
                />
              )}
              <Fact label="Events" value={summary.eventCount} />
              <Fact
                label="Warnings / Errors"
                value={
                  <span className="tabular-nums">
                    <span
                      className={summary.warningCount ? 'text-amber-600 dark:text-amber-400' : ''}
                    >
                      {summary.warningCount}
                    </span>
                    {' / '}
                    <span className={summary.errorCount ? 'text-red-600 dark:text-red-400' : ''}>
                      {summary.errorCount}
                    </span>
                  </span>
                }
              />
              <Fact
                label="GitHub run"
                value={
                  summary.runId ? (
                    <a
                      href={`https://github.com/ApptonomyInc/a7y/actions/runs/${summary.runId}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-zinc-700 dark:text-zinc-300 hover:underline"
                    >
                      {summary.runId} <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : (
                    <span className="text-zinc-400">local</span>
                  )
                }
              />
            </div>
          </div>
          {summary.terminalMessage && (
            <div className="mt-3 border-t border-zinc-100 dark:border-zinc-800 pt-2 text-xs text-zinc-500 font-mono break-all">
              {summary.terminalMessage}
            </div>
          )}
        </div>
      )}

      {summary && <SessionQuotaUsage accounts={detail.data?.quotaAccounts ?? []} />}

      {detail.data?.throughput && <SessionThroughputSection throughput={detail.data.throughput} />}

      {/* A running session that has not reached Part B yet has no structured
          ticket rows, so `deriveTickets` falls back to scraping ids out of log
          prose — which surfaces epic parents and trigger probes that were never
          work items. Say so instead of showing a misleading list. */}
      {summary?.outcome === 'running' &&
        tickets.length > 0 &&
        tickets.every((t) => t.outcome === 'mentioned') && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 px-3 py-2 text-xs text-zinc-500">
            Batch not logged yet — sessions record their tickets at the start of Part B, a minute or
            two in. The ids mentioned in the log so far are not necessarily this session's work.
          </div>
        )}

      {tickets.length > 0 &&
        !(summary?.outcome === 'running' && tickets.every((t) => t.outcome === 'mentioned')) && (
          <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-b-2 border-zinc-200 bg-zinc-100/80 px-4 py-3 dark:border-zinc-700 dark:bg-zinc-950/65">
              <span className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-600 dark:text-zinc-300">
                Tickets ({tickets.length})
              </span>
              {/* The run-history list shows only the RESOLVED subset, so the two
                pages legitimately disagree — 13 vs 17 on APD 2026-08-03-00-39.
                Spelling the split out here is what makes that read as a
                breakdown rather than a data inconsistency. */}
              {outcomeBreakdown(tickets).map(({ outcome, n }) => (
                <span
                  key={outcome}
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-500 dark:text-zinc-400"
                  title={TICKET_OUTCOMES[outcome]?.hint}
                >
                  <span
                    className={`inline-block h-2 w-2 rounded-full ${
                      OUTCOME_DOT[TICKET_OUTCOMES[outcome]?.tone ?? 'carried']
                    }`}
                  />
                  <span className="tabular-nums font-medium text-zinc-700 dark:text-zinc-300">
                    {n}
                  </span>
                  {ticketOutcomeLabel(outcome)}
                </span>
              ))}
            </div>
            <div>
              {ticketSections.map((section, sectionIndex) => (
                <div
                  key={section.key}
                  className={
                    sectionIndex > 0 ? 'border-t-2 border-zinc-200 dark:border-zinc-700' : ''
                  }
                >
                  <div
                    className={`flex flex-wrap items-baseline gap-x-3 gap-y-1 px-4 py-2 ${TICKET_SECTION_HEADER_STYLES[section.key]}`}
                  >
                    <span
                      className={`text-xs font-bold uppercase tracking-[0.1em] ${TICKET_SECTION_LABEL_STYLES[section.key]}`}
                    >
                      {section.label} ({section.tickets.length})
                    </span>
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">
                      {section.description}
                    </span>
                  </div>
                  <div className="divide-y divide-zinc-200/80 bg-white dark:divide-zinc-800 dark:bg-zinc-900">
                    {section.tickets.map((t) => (
                      <div
                        key={t.id}
                        className="flex min-h-11 items-center gap-3 px-4 py-2 transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800/55"
                      >
                        <div className="flex shrink-0 flex-col leading-tight">
                          <a
                            href={`https://linear.app/apptonomy/issue/${t.id}`}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono text-xs text-zinc-800 dark:text-zinc-200 hover:underline inline-flex items-center gap-1"
                          >
                            {t.id} <ExternalLink className="h-3 w-3 text-zinc-400" />
                          </a>
                          {/* The D1 drill-in: which bundle/attempt/account
                              admitted this ticket, when a fact named one. */}
                          {ticketBundleLine(t) && (
                            <span
                              className="font-mono text-[10px] text-zinc-400"
                              title="bundle · attempt · implementation account"
                            >
                              {ticketBundleLine(t)}
                            </span>
                          )}
                          {ticketAdmittedLine(t) && (
                            <span
                              className="text-[10px] text-zinc-400 whitespace-nowrap"
                              title="When this cycle admitted the ticket to a lane"
                            >
                              {ticketAdmittedLine(t)}
                            </span>
                          )}
                        </div>
                        <span
                          className="text-xs text-zinc-600 dark:text-zinc-400 truncate grow"
                          title={t.title ?? undefined}
                        >
                          {t.title ?? (
                            <span className="italic text-zinc-400">
                              mentioned in log — title not recorded
                            </span>
                          )}
                        </span>
                        {t.priority ? (
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${PRIORITY_STYLES[t.priority] ?? PRIORITY_STYLES.Low}`}
                            title={`Linear priority: ${t.priority}`}
                          >
                            {t.priority}
                          </span>
                        ) : null}
                        <span
                          className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium shrink-0 ${TICKET_OUTCOME_STYLES[t.outcome] ?? TICKET_OUTCOME_DEFAULT}`}
                          title={
                            TICKET_OUTCOMES[t.outcome]?.hint ?? `Outcome recorded as "${t.outcome}"`
                          }
                        >
                          {ticketOutcomeLabel(t.outcome)}
                        </span>
                        {ticketStageChip(t, now)}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

      {events.length > 0 && (
        <div className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
          <div className="px-3 py-2 text-[11px] uppercase tracking-wide text-zinc-400 border-b border-zinc-100 dark:border-zinc-800">
            Log ({events.length} {events.length === 1 ? 'event' : 'events'}){' '}
            {localZoneAbbrev() && `— ${localZoneAbbrev()}`}
          </div>
          <div>
            {groupEvents(kind as AutobuildKind, events, summary?.outcome !== 'running').map(
              (group, gi) => {
                const { warnings, errors } = severityCounts(group.events);
                return (
                  <div
                    key={`${group.phase}-${gi}`}
                    className="border-b border-zinc-100 last:border-b-0 dark:border-zinc-800"
                  >
                    <div className="bg-zinc-50 px-3 py-2 dark:bg-zinc-900/60">
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                        {/* Position in the session's documented run order. Parts and
                          Phases are one flat sequence — Part A precedes Phase 1 —
                          so a step number is the honest way to show where a
                          section sits; nesting would invert the real order. */}
                        {group.info?.step != null && (
                          <span
                            className="shrink-0 rounded bg-zinc-200 px-1.5 py-0.5 font-mono text-[10px] font-medium text-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                            title={`Step ${group.info.step} of ${group.info.totalSteps} in the ${KIND_LABELS[kind]} run order`}
                          >
                            {group.info.step}/{group.info.totalSteps}
                          </span>
                        )}
                        <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                          {group.info?.title ?? group.phase}
                        </span>
                        <span className="font-mono text-[11px] text-zinc-400">{group.phase}</span>
                        <span
                          className="text-[11px] text-zinc-400"
                          title={utcTooltip(Date.parse(group.events[0].ts))}
                        >
                          {fmtLogTime(group.events[0].ts)}
                          {group.endMs != null && `–${fmtLocalClock(group.endMs)}`}
                          {' · '}
                          {group.events.length} {group.events.length === 1 ? 'event' : 'events'}
                        </span>
                        {/* Duration only once the phase has handed off; the open
                          phase of a running session has no end yet. */}
                        {group.endMs != null ? (
                          <span
                            className="shrink-0 rounded bg-zinc-200/70 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-zinc-600 dark:bg-zinc-800/70 dark:text-zinc-300"
                            title="Time from this phase's first row until the next phase began"
                          >
                            {fmtDuration((group.endMs - group.startMs) / 1000)}
                          </span>
                        ) : (
                          <span className="text-[10px] text-amber-600 dark:text-amber-400">
                            in progress
                          </span>
                        )}
                        {errors > 0 && (
                          <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-medium text-red-800 dark:bg-red-950 dark:text-red-300">
                            {errors} error{errors === 1 ? '' : 's'}
                          </span>
                        )}
                        {warnings > 0 && (
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                            {warnings} warning{warnings === 1 ? '' : 's'}
                          </span>
                        )}
                      </div>
                      {group.info && (
                        <p className="mt-0.5 max-w-3xl text-xs text-zinc-500 dark:text-zinc-400">
                          {group.info.description}
                        </p>
                      )}
                    </div>
                    <div className="divide-y divide-zinc-50 dark:divide-zinc-900">
                      {group.events.map((e, i) => (
                        <div
                          key={`${e.ts}-${i}`}
                          className={`flex gap-3 border-l-2 px-3 py-1.5 ${SEVERITY_ROW[e.severity]}`}
                        >
                          <span
                            className="shrink-0 pt-0.5 font-mono text-[11px] text-zinc-400 tabular-nums"
                            title={utcTooltip(Date.parse(e.ts))}
                          >
                            {fmtLogTime(e.ts)}
                          </span>
                          {/* Only shown when it differs from the section — a ticket or
                            state-change row inside a part, never 40 repeats of the
                            section's own slug. */}
                          <span
                            className="w-20 shrink-0 truncate pt-0.5 font-mono text-[11px] text-zinc-400"
                            title={e.phase}
                          >
                            {normalizePhase(e.phase) === group.phase ? '' : e.phase}
                          </span>
                          <span className="font-mono text-xs break-words whitespace-pre-wrap">
                            {e.message}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      )}

      {summary && events.length === 0 && (
        <div className="p-6 text-sm text-zinc-500">No log events recorded for this session.</div>
      )}
    </div>
  );
}
